import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useSessionContext } from '../../../../../../../../contexts/SessionContext';
import { ExtendedListing } from '../../../../../../../../models/Listing';
import { Organization } from '../../../../../../../../types/api.graphql';
import OrganizationDropdown from '../../common';
import VehicleInformation from '../../VehicleInformation/VehicleInformation';
import { Container, EmailInput } from './ShareWithEmailStep.styles';

interface ShareWithEmailStepProps {
  listing?: ExtendedListing;
  listings?: ExtendedListing[];
  email: string;
  setEmail: (email: string) => void;
  organizationFrom: Organization;
  setOrganizationFrom: (organization: Organization) => void;
  customVehicleInfo?: React.ReactNode;
}

const ShareWithEmailStep = ({
  listing,
  listings,
  email,
  setEmail,
  organizationFrom,
  setOrganizationFrom,
  customVehicleInfo,
}: ShareWithEmailStepProps) => {
  // Dependencies
  const { t } = useTranslation();
  const { me } = useSessionContext();

  // Derived states
  const availableOrgs = me?.organizations;

  const displayShareFromDropdown = () => {
    // Handle normal share
    if (listing) {
      if (!listing.shared) return false;
      if (!listing.receivingUser) return false;
      return true;
    }

    // Handle bulk share case
    if (listings?.length) {
      // Show dropdown if at least one listing has receivingUser or is shared
      return listings.some((listing) => listing.shared && listing.receivingUser);
    }

    return false;
  };

  const getShareFromTooltip = () => {
    // Use different tooltip text for bulk share vs single listing
    if (listings?.length) {
      return t('vdp.shareModal.selectOrgFromTooltipBulkToEmail');
    }
    return t('vdp.shareModal.selectOrgFromTooltip');
  };

  return (
    <Container>
      {customVehicleInfo || <VehicleInformation listing={listing} />}
      <EmailInput
        label={t('common.email.label')}
        placeholder={t('common.email.placeholder')}
        value={email}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
      />
      {displayShareFromDropdown() ? (
        <OrganizationDropdown
          availableOrganizations={availableOrgs}
          selectedOrganization={organizationFrom}
          setSelectedOrganization={setOrganizationFrom}
          disableDropdown={listing ? !!listing?.receivingOrganization : false}
          label={t('vdp.shareModal.shareFrom')}
          infoTooltip={getShareFromTooltip()}
        />
      ) : null}
    </Container>
  );
};

export default ShareWithEmailStep;
